import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User,
    UserCredential
} from 'firebase/auth';
import { auth } from '../firebase/firebase.config';
import { createUserDocument, getUserDocument, updateUserDocument, UserData } from '../services/userService';
import { revenueCatService } from '../services/revenueCatService';
import analyticsService from '../services/analyticsService';

interface AuthContextType {
    currentUser: User | null;
    userData: UserData | null;
    loading: boolean;
    signup: (email: string, password: string, fullName: string) => Promise<UserCredential>;
    login: (email: string, password: string) => Promise<UserCredential>;
    logout: () => Promise<void>;
    refreshUserData: () => Promise<void>;
    updateUserProfile: (data: Partial<UserData>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchUserData = async (uid: string) => {
        try {
            const userDoc = await getUserDocument(uid);
            setUserData(userDoc);
        } catch (error) {
            console.error('Error fetching user data:', error);
            setUserData(null);
        }
    };

    const refreshUserData = async () => {
        if (currentUser) {
            await fetchUserData(currentUser.uid);
        }
    };

    const signup = async (email: string, password: string, fullName: string) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Create user document in Firestore
        await createUserDocument(userCredential.user.uid, email, fullName);

        // Register user with RevenueCat (including email)
        try {
            await revenueCatService.registerUser(userCredential.user.uid, email, {
                displayName: fullName
            });
            console.log('✅ User registered with RevenueCat successfully');
        } catch (error) {
            console.error('⚠️ Failed to register user with RevenueCat:', error);
            // Don't throw error here as the main signup was successful
        }

        // Fetch the newly created user data
        await fetchUserData(userCredential.user.uid);

        // Track signup event
        analyticsService.trackSignUp('email');

        return userCredential;
    };

    const login = async (email: string, password: string) => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // Ensure user is registered with RevenueCat on login
        try {
            const userData = await getUserDocument(userCredential.user.uid);
            if (userData) {
                await revenueCatService.registerUser(userCredential.user.uid, userData.email, {
                    displayName: userData.fullName
                });
                console.log('✅ User email synced with RevenueCat on login');
            }
        } catch (error) {
            console.error('⚠️ Failed to sync user with RevenueCat on login:', error);
            // Don't throw error here as the main login was successful
        }

        // Track login event
        analyticsService.trackLogin('email');

        return userCredential;
    };

    const logout = async () => {
        await signOut(auth);
        setUserData(null);
    };

    const updateUserProfile = async (data: Partial<UserData>) => {
        if (!currentUser) {
            throw new Error('No user is currently logged in');
        }

        await updateUserDocument(currentUser.uid, data);
        
        // Sync changes with RevenueCat if email or name changed
        try {
            const updatedAttributes: { email?: string; displayName?: string } = {};
            
            if (data.email) {
                updatedAttributes.email = data.email;
            }
            if (data.fullName) {
                updatedAttributes.displayName = data.fullName;
            }
            
            if (Object.keys(updatedAttributes).length > 0) {
                await revenueCatService.setUserAttributes(updatedAttributes);
                console.log('✅ User profile changes synced with RevenueCat');
            }
        } catch (error) {
            console.error('⚠️ Failed to sync profile changes with RevenueCat:', error);
            // Don't throw error here as the main profile update was successful
        }
        
        await refreshUserData(); // Refresh user data after update
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            if (user) {
                // Fetch user data when user is authenticated
                await fetchUserData(user.uid);
            } else {
                setUserData(null);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userData,
        loading,
        signup,
        login,
        logout,
        refreshUserData,
        updateUserProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}; 